import React from 'react';
import { Box, ExpandableSection, SpaceBetween } from '@cloudscape-design/components';

const introduction = "This report uses current operational records held in PATH. A unique client means one PATH client record, regardless of how many applications or action plans it has. Separate duplicate client records are not automatically merged.";
const periods = "The fiscal year is April 1–March 31. Quarters are April–June, July–September, October–December and January–March. Choose a fiscal year, full year or quarter, and provinces/territories. Leave the province selection empty for the national view, including MSL interactions recorded at Head Office. For a current period, Clients served, Clients employed, Clients returned to school and Applications received stop at today, using Toronto’s calendar date. Funded clients and Scheduled funding cover the full selected period, including future scheduled amounts.";
const sections = [
  {
    "title": "Clients served",
    "paragraphs": [
      [
        "Calculation",
        "The number of unique clients who submitted a full application during the reporting period, plus every recorded Minimum Service Level (MSL) interaction during that period. Multiple applications by the same client count once. Each MSL interaction adds one, even when the person had other MSL interactions or also submitted an application. This is therefore not a count of entirely distinct people, nor of everyone with an ongoing intervention."
      ],
      [
        "Province / territory",
        "Applicants are selected using the province stored on their application, falling back to the submitted intake information. MSL interactions use the region recorded against the interaction, which can be NWAC Head Office. These are two different location bases; MSL region does not necessarily represent the person’s home address."
      ],
      [
        "Period",
        "Applications use their submission date. MSL uses the recorded interaction date, rather than the date someone entered it into PATH."
      ]
    ]
  },
  {
    "title": "Clients employed",
    "paragraphs": [
      [
        "Calculation",
        "The number of unique clients with at least one action plan recording Employed (result code 2) or Self-employed (result code 3) with a result date inside the reporting period. A client counts once even with multiple qualifying plans or both types of employment result. Draft and archived action plans are excluded. This measures recorded employment outcomes, rather than everyone currently employed."
      ],
      [
        "Province / territory",
        "Uses the client’s current address when the report is run. It does not use an address preserved from when the application, action plan or employment result began. Updating a client’s province can therefore move an earlier outcome between provincial totals."
      ],
      [
        "Period",
        "Uses the action plan’s result date. The application and action plan can have started before the reporting period. The plan does not have to be marked Completed to count if it otherwise meets the conditions above."
      ]
    ]
  },
  {
    "title": "Clients returned to school",
    "paragraphs": [
      [
        "Calculation",
        "The number of unique clients with at least one action plan recording Returned to school (result code 4) with a result date inside the reporting period. Multiple qualifying school results count once. Draft and archived plans are excluded. A client can appear in both this total and Clients employed if they have qualifying results in both categories."
      ],
      [
        "Province / territory",
        "Uses the client’s current address when the report is run, with the same possibility of historical provincial totals changing after an address update."
      ],
      [
        "Period",
        "Uses the action plan’s result date, rather than its start date, the application date or a separately recorded school-start date."
      ]
    ]
  },
  {
    "title": "Applications received",
    "paragraphs": [
      [
        "Calculation",
        "The number of full applications submitted during the reporting period. Each application counts, including repeat applications by the same client. An application remains included regardless of whether it subsequently becomes approved, denied or withdrawn. Unsubmitted drafts and MSL interactions do not count."
      ],
      [
        "Province / territory",
        "Uses the province stored on the application, falling back to its submitted intake information. It does not substitute the client’s current profile address. This is the application information currently held in PATH, rather than a separately frozen historical report snapshot."
      ],
      [
        "Period",
        "Uses the submission date, irrespective of when the application was assessed or decided."
      ]
    ]
  },
  {
    "title": "Funded clients",
    "paragraphs": [
      [
        "Calculation",
        "The number of unique clients with at least one qualifying positive scheduled funding amount due during the reporting period, using the same calculation as Financial Reports. A client counts once across their qualifying applications, interventions, payment instalments and funding streams. Having an intervention that runs through the period is insufficient if no funding is scheduled within it. See Shared funding qualifications below for eligibility and exclusions."
      ],
      [
        "Province / territory",
        "Uses the assigned budget pot’s region. Moving home does not move existing funding. A client funded by more than one region counts once within each selected regional scope and once in the combined national count."
      ],
      [
        "Period",
        "Uses scheduled payment dates. Approval can have occurred in an earlier period. For a current year or quarter, this total includes qualifying funding scheduled later in that selected period, including dates after today."
      ]
    ]
  },
  {
    "title": "Scheduled funding",
    "paragraphs": [
      [
        "Calculation",
        "The sum, in Canadian dollars, of all qualifying positive funding instalments scheduled within the reporting period, across CRF and EI. Every qualifying instalment contributes its amount, even though its client counts only once in Funded clients. This is scheduled funding, not confirmation that money has been paid. See Shared funding qualifications below for eligibility and exclusions."
      ],
      [
        "Province / territory",
        "Uses the assigned budget pot’s region, matching Funded clients and Financial Reports. Unassigned pot retains funding that needs a pot assignment, without guessing from the client’s address."
      ],
      [
        "Period",
        "Each instalment belongs to the period containing its scheduled payment date. Recurring payments are allocated individually; the intervention’s entire amount is not repeated in every period it spans. Where schedules are incomplete, the shared financial rules can use payment-type timing or the intervention start date. Where itemised funding lines are missing, they can allocate the recorded approved amount to the intervention start date. Amounts with no usable allocation date cannot enter a period total. The full selected period is included, even when some scheduled dates are after today."
      ]
    ]
  },
  {
    "title": "Shared funding qualifications",
    "paragraphs": [
      [
        "Eligibility",
        "The existing financial rules include qualifying original draft interventions with positive scheduled funding, as well as approved, in-progress, suspended and completed interventions. Pending draft revisions and archived intervention/action-plan records are excluded. Zero-dollar and negative funding lines contribute neither a funded client nor a funding amount."
      ],
      [
        "Application links",
        "Eligible funding is included regardless of whether it was entered through an application or as a historical record. A missing application link alone does not exclude it. A denied or withdrawn application does not automatically remove a qualifying intervention’s funding schedule. Funding issues and fallbacks are flagged by the shared calculation."
      ]
    ]
  },
  {
    "title": "Comparing totals and reports",
    "paragraphs": [
      [
        "Different populations",
        "Someone can have an employment outcome or scheduled funding this year after applying last year, without appearing among this year’s applicant-based Clients served. Employment and school outcomes can overlap. The six totals are not parts of a single total and should not be added together."
      ],
      [
        "Unique counts are not necessarily additive",
        "Adding quarterly counts can count the same client repeatedly. Separate provincial applicant counts can also overlap where a client submitted applications in different provinces. Unique clients are counted again within the combined selection, rather than by adding separate totals."
      ],
      [
        "Matching financial filters",
        "Compare the same fiscal period and provinces, with all case managers and both funding streams selected in Financial Reports. The Activity Report checks that its scheduled funding and funded-client totals reconcile with that shared calculation."
      ],
      [
        "Historical totals can change",
        "Reports use the records currently held in PATH. Corrections, address changes, revised funding schedules and archiving can change a previously reported period. All regions includes Unassigned pot funding; selecting an individual budget region excludes funding outside that region. Address changes can affect activity geography, but do not move funding between budget regions."
      ]
    ]
  },
  {
    "title": "Viewing details and exporting",
    "paragraphs": [
      [
        "Details",
        "Select a total to open its detail table. Clients served separates distinct applicants from MSL interactions. Search, sort or change pages; select a name to inspect the contributing records and open their PATH case or application. Close details returns to the six measures. A detail search does not change the overall summary totals."
      ],
      [
        "Excel",
        "Export to Excel includes the summary and every contributing detail list: Summary plus seven detail sheets. Export details includes the summary and every matching row from the current detail search, across all pages. Exports record the selected scope and generation time. No milestones, targets or narrative are included."
      ],
      [
        "Refresh",
        "Refresh reloads current data. Failed loads show an error, never zero totals."
      ]
    ]
  }
];

const ActivityReportHelp = () => <SpaceBetween size="m">
  <Box>{introduction}</Box>
  <Box>{periods}</Box>
  {sections.map(section => <ExpandableSection key={section.title} headerText={section.title}>
    <SpaceBetween size="s">
      {section.paragraphs.map(([label, text]) => <Box key={label}><strong>{label}: </strong>{text}</Box>)}
    </SpaceBetween>
  </ExpandableSection>)}
</SpaceBetween>;

// Keep the assistant's reference identical to the staff-facing counting guidance.
ActivityReportHelp.aiContext = [
  'Activity Report at /reporting/activity-report.', introduction, periods,
  ...sections.map(section => `${section.title}. ${section.paragraphs.map(([label, text]) => `${label}: ${text}`).join(' ')}`),
].join('\n\n');
export default ActivityReportHelp;
